<template>
  <!-- This wrapper div is required to satisfy the singular root node requirements -->
  <div
    data-testid="feature-folder-container"
    class="feature-folder-container"
  >
    <FolderName
      v-if="folderItem.name && (folderItem.defaultVisibility || canEdit)"
      :folder="folderItem"
      :folder-name="folderItem.name"
      :depth="depth"
      :drag-handler="dragHandler"
      :drop-handler="dropHandler"
      :can-edit="canEdit"
      :show-body="folderItem.showBody"
      @updateFolder="(key, value) => overlay.updateFolder(folderIndex, key, value)"
      @toggleFolderVisibility="handleFolderVisibility"
      @deleteFolder="deleteFolder"
      @panToFirstFeature="panToFirstFeature"
    />
    <!-- If the 'folder' has an 'id' it is a feature,
   this conditional covers the case for 'Features' with no folder in the overlay.folders value-->
    <FeatureResultCard
      v-if="featureVisibleInViewMode(folderItem.id) && !nestsUnderSource"
      :depth="depth"
      :data-testid="`resultcard-${folderItem.id}`"
      :feature="getFeature(folderItem.id)"
      :drag-handler="dragHandler"
      :drop-handler="dropHandler"
      :can-edit="canEdit"
      :has-linked-labels="linkedLabels.length > 0"
      :linked-labels-expanded="linkedLabelsExpanded"
      @deleteFeature="deleteFeature"
      @toggleLinkedLabels="toggleLinkedLabels"
    />
    <template v-if="linkedLabelsExpanded">
      <FeatureResultCard
        v-for="label in linkedLabels"
        :key="label.id"
        :depth="depth + 1"
        :data-testid="`resultcard-${label.id}`"
        :feature="label"
        :can-edit="canEdit"
        drag-disabled
        @deleteFeature="deleteFeature"
      />
    </template>
    <div
      v-if="folderItem.showBody && folderItem.children && (folderItem.defaultVisibility || canEdit)"
      style="width: 100%;"
    >
      <FeatureFolder
        v-for="(child, index) in folderItem.children.filter((c) => c)"
        :key="child?.folderID ?? child?.id"
        :data-testid="child?.folderID ?? child?.id"
        :folder-item="child ?? {}"
        :depth="depth + 1"
        :overlay="overlay"
        :folder-index="folderIndex.concat(index)"
        :can-edit="canEdit"
        @deleteFeature="deleteFeature"
      />
    </div>
    <div
      v-if="folderIndex.length === 1 && folderIndex[0] === overlay.folders.length - 1"
      :style="`opacity: ${rootHover ? '1' : '0'}`"
      class="drop-root"
      @dragenter.prevent
      @dragover.prevent="rootHover = true"
      @dragleave.prevent="rootHover = false"
      @drop.prevent="(e) => dropHandler(e, 0, true)"
    />
  </div>
</template>

<script>
// 3rd party imports
import {mapMutations} from 'vuex';

// helpers and services
import {Overlay, OverlayService} from '@/services/OverlaysService';
import {SharedMap} from '@/lib/classes/SharedMap';

import {linkedLabelSource, linkedLabelsOf} from './linkedLabelNesting';

// components
import FeatureResultCard from './sections/FeatureResultCard.vue';
import FolderName from './sections/FolderName.vue';

/** @typedef {import('@/services/OverlaysService/Feature').Feature} Feature */

export default {
  name: 'FeatureFolder',
  components: {
    FeatureResultCard,
    FolderName,
  },
  props: {
    folderItem: {
      type: Object,
      required: true,
    },
    depth: {
      type: Number,
      default: 0,
    },
    overlay: {
      type: Object,
      required: true,
      validator: function(value) {
        return value instanceof Overlay || value instanceof SharedMap;
      },
    },
    folderIndex: {
      type: Array,
      default: () => [0],
    },
    canEdit: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['setFolderLoading'],
  data() {
    return {
      rootHover: false,
    };
  },
  computed: {
    /**
     * @return {boolean}
     */
    nestsUnderSource() {
      const source = linkedLabelSource(this.getFeature(this.folderItem.id), this.overlay.features);
      return !!source && this.featureVisibleInViewMode(source.id);
    },
    /**
     * @return {Feature[]}
     */
    linkedLabels() {
      if (!this.folderItem.id || !this.featureVisibleInViewMode(this.folderItem.id)) return [];
      return linkedLabelsOf(this.overlay.features, this.folderItem.id)
        .filter((label) => label.defaultVisibility || this.canEdit);
    },
    /**
     * Expanded by default so new labels show right away.
     * @return {boolean}
     */
    linkedLabelsExpanded() {
      return this.linkedLabels.length > 0 && this.folderItem.showBody !== false;
    },
  },
  methods: {
    ...mapMutations(['setSnack']),
    toggleLinkedLabels() {
      this.overlay.updateFolder(this.folderIndex, 'showBody', !this.linkedLabelsExpanded);
    },
    /**
     * @param {string} id
     * @return {Feature}
     */
    getFeature(id) {
      return this.overlay.features.get(id) || null;
    },
    panToFirstFeature() {
      if (!this.folderItem.children) return;

      const firstFeatureId = this.folderItem.children.find((child) => child.id)?.id;
      if (!firstFeatureId) return;

      OverlayService.panAndZoomToFeature(this.getFeature(firstFeatureId));
    },
    handleFolderVisibility() {
      this.overlay.toggleFolderVisibility(this.folderIndex, !this.folderItem.visible, this.canEdit);
    },
    featureVisibleInViewMode(featureId) {
      const feature = this.getFeature(featureId);
      if (!feature) return false;
      return feature.defaultVisibility || this.canEdit;
    },
    /**
     * Emitted from a Feature Result card to handle deleting a selected feature from an Overlay.
     * @param {Object} selectedFeature - The feature to delete.
     */
    async deleteFeature(selectedFeature) {
      const restoreFn = await this.overlay.deleteFeature(selectedFeature);
      if (restoreFn !== false) {
        this.setSnack({message: `Feature has been deleted`, action: {text: 'Undo', callback: restoreFn}});
      }
    },
    async deleteFolder() {
      // Set folder loader for overlayEditSubmenu
      this.$emit('setFolderLoading', true);
      const restoreFolder = await this.overlay.deleteFolder(this.folderIndex);
      const restoreFn = async () => {
        this.$emit('setFolderLoading', true);
        await restoreFolder();
        this.$emit('setFolderLoading', false);
      };
      if (restoreFolder) {
        this.setSnack({message: `Folder has been deleted`, action: {text: 'Undo', callback: restoreFn}});
      }
      this.$emit('setFolderLoading', false);
    },
    /**
     * Fired on '@drop' event from a folder to handle moving a folder item into a folder
     * @param {Event} e - The event object originating from the element that was
     * @param {number} offset - Number indicating if the drop should be registered as being
     * 'above', 'below' or 'inside' of a dropped item
     * @param {boolean} root - If true, handle the move separately to the root of the folder data structure
     */
    dropHandler(e, offset, root=false) {
      this.rootHover = false;
      // Get the `path` string value set on the dragged element event object
      let path = e.dataTransfer.getData('path');
      // Reconstruct the path array of numbers from the text value path
      path = path.split(',').map((i) => parseInt(i));
      // Get the total depth (including children) of the dragged item
      const movedItem = this.overlay.getFolderItem(path).itemAtIndex;
      const movedDepth = this.overlay.folderDepth(movedItem);
      const newDepth = this.depth + movedDepth;
      // The following is to coincide with the max level of depth supported by firestore.
      // Don't allow folders to be moved if the move would exceed the max depth.
      // The capped depth for folders should be one less than the max to avoid useless empty folders at max depth
      if ((movedItem.children && newDepth > 7) || newDepth > 8) {
        this.setSnack({message: `This operation exceeds the maximum folder depth`});
        return;
      }
      // Ensure that the dragged item is allowed to be moved into the dropped item
      if (!this.overlay.isValidFolderOperation(path, this.folderIndex)) return;
      // Get the folder item that corresponds to the item at the dropped index
      const toPath = this.overlay.getFolderItem(this.folderIndex);
      // Remove the item from the moved item's parent folder
      this.overlay.removeFolderItem(path);
      if (!toPath || !movedItem) return;
      // Handle moves to the root and return
      if (root) {
        // Add the removed item into the dropped folder
        this.overlay.addFolderItem(this.overlay.folders, movedItem, null);
        return;
      }
      let toIndex = this.folderIndex[this.folderIndex.length - 1];
      const fromIndex = path[path.length - 1];
      // Check to see if the item is being moved within the same folder
      const sameFolder =
      path.length === this.folderIndex.length &&
      (path.length === 1 ||
        path.slice(0, path.length - 1).every((val, index) => val === this.folderIndex[index])
      );
      const aboveSiblingOffset = toIndex < fromIndex && offset > 0;
      const belowSiblingOffset = toIndex > fromIndex && offset < 0;
      const newFolderOffset = !sameFolder && offset > 0;
      const dropIntoFolder = offset === 0;
      // Apply an offset to the `toIndex` if the item is NOT being dropped into a folder (offset === 0) and is
      // EITHER
      // 1) In the same folder AND EITHER
      //   a) being moved above a sibling feature with an offset of 1
      //   b) being moved below a sibling feature with an offset of -1
      // 2) Being moved to a new folder with an offset of 1
      if ((!dropIntoFolder && (sameFolder && (aboveSiblingOffset || belowSiblingOffset))) || newFolderOffset) {
        toIndex += offset;
      }
      // If the offset is '0', it has been dropped into a folder, otherwise just use the adjusted `toIndex` value
      this.overlay.addFolderItem(
        offset === 0 ? toPath.itemAtIndex : toPath.parentFolder,
        movedItem,
        offset === 0 ? null : toIndex,
      );
      return;
    },
    /**
     * Sets data on the drag event so it can be read from the '@drop' event
     * @param {Event} e - The initiating drag event
     * @param {number[]} path - The path within the folder structure to the item being dragged
     */
    dragHandler(e) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.dropEffect = 'move';
      e.dataTransfer.setData('path', this.folderIndex);
    },
  },
};

</script>
<style lang="scss" scoped>
.feature-folder-container {
  width: 100%;
}

.drop-root {
  width: 100%;
  background: linear-gradient(main.$linkBlue, white);
  height: 7px;
}
</style>
